import { forwardRef } from 'react';

import { composeRenderProps } from 'react-aria-components/composeRenderProps';
import {
  ColumnResizer,
  Column as RACColumn,
  type ColumnProps as RACColumnProps,
} from 'react-aria-components/Table';

import { cx } from '../../utils/cx';
import { Icon } from '../icon/icon';
import { alignStyles, type TableAlign } from './align';

import styles from './table_view.module.css';

export type ColumnProps = RACColumnProps & {
  className?: string;
  allowsResizing?: boolean;
  fixedLeft?: boolean;
  fixedRight?: boolean;
  align?: TableAlign;
};

/**
 * Column - A table column header with optional sorting.
 */
export const Column = forwardRef<HTMLTableCellElement, ColumnProps>(
  ({ align, className, children, ...props }, ref) => {
    return (
      <RACColumn
        ref={ref}
        className={({ sortDirection }) =>
          cx([
            styles.column,
            { [styles.column_active_sort]: !!sortDirection },
            { [styles.fixed_left]: props.fixedLeft },
            { [styles.fixed_right]: props.fixedRight },
            align ? alignStyles[align] : undefined,
            className,
          ])
        }
        {...props}
      >
        {composeRenderProps(
          children,
          (childrenContent, { allowsSorting, sortDirection }) => {
            const content = allowsSorting ? (
              <div className={styles.column_content}>
                <span>{childrenContent}</span>
                <div className={styles.sort_indicator}>
                  {sortDirection ? (
                    <Icon
                      name={
                        sortDirection === 'ascending'
                          ? 'arrow_small_n'
                          : 'arrow_small_s'
                      }
                      className={styles.sort_icon}
                      data-active={true}
                    />
                  ) : (
                    <Icon
                      name="sort"
                      className={styles.sort_icon}
                      data-active={false}
                    />
                  )}
                </div>
              </div>
            ) : (
              childrenContent
            );

            return (
              <>
                {content}
                {props.allowsResizing && (
                  <ColumnResizer className={styles.column_resizer} />
                )}
              </>
            );
          },
        )}
      </RACColumn>
    );
  },
);
